import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { Box, Text } from 'core/components';
import { buildFilterGroup } from 'core/util/filters';
import makeCancelable, { CanceledError } from 'core/util/cancelablePromise';

// see comments in loadFlowMetricsData for rationale behind these limits
// tl;dr: when grouping by provider, we want to include as many devices as possible to get accurate provider totals,
// but when not grouping by provider we can merge by device to get accurate plan-level totals
// without needing to include a large number of individual devices
// The values below are harmless defaults, BUT avoid setting them below ~10
// since the fastbit SQL generator uses them to compute an internal sublimit that
// caps raw data rows fetched per group.
const MERGE_DEVICE_LIMIT = 20; // Could be any non-zero number but > 10 is recommended, this is devices to include in merge when NOT grouping by provider (License page)
export const NON_MERGE_DEVICE_LIMIT = 500; // Max number of devices to include when grouping by provider (Public Clouds page)
/**
 * CloudPakFlowMetricsWrapper
 *
 * Fetches flow metrics data (raw, received, stored) from kmetrics API.
 * Supports both single-plan queries (License page) and multi-provider queries (Public Clouds page).
 *
 * Props:
 * - plan_id: (required) The plan ID to query metrics for
 * - lookback: (optional) Time period: '1_day', '7_days', or '30_days'. Default: '1_day'
 * - groupByProvider: (optional) When true, adds cloud_provider dimension and returns provider-separated data
 * - children: Render function that receives query results
 *
 * Children function receives:
 * - loading: Boolean indicating if queries are in progress
 * - rawFlowModel: Raw flow data model
 * - receivedFlowModel: Received flow data model
 * - storedFlowModel: Stored flow data model
 * - rawFlowByProvider: (only when groupByProvider=true) Object with provider-separated raw flow data
 * - receivedFlowByProvider: (only when groupByProvider=true) Object with provider-separated received flow data
 * - storedFlowByProvider: (only when groupByProvider=true) Object with provider-separated stored flow data
 * - rawFlowQuery: Query object for raw flow (for use with MetricsExplorerButton)
 * - receivedFlowQuery: Query object for received flow (for use with MetricsExplorerButton)
 * - storedFlowQuery: Query object for stored flow (for use with MetricsExplorerButton)
 * - error: Error message if queries failed
 * - retry: Function to retry loading data
 */
@inject('$query', '$clouds', '$setup')
@observer
export default class CloudPakFlowMetricsWrapper extends Component {
  // Mapping from simple values to ISO 8601 duration format
  timePeriodToLookback = {
    '1_day': 'PT86400S',
    '7_days': 'PT604800S',
    '30_days': 'PT2592000S'
  };

  // Mapping from time period to the binning window size (seconds) used for received/stored flow queries.
  // Larger lookbacks use coarser bins so the number of points stays manageable.
  timePeriodToWindowSize = {
    '1_day': 60,
    '7_days': 600,
    '30_days': 10800
  };

  state = {
    loading: true,
    rawFlowModel: null,
    receivedFlowModel: null,
    storedFlowModel: null,
    error: null
  };

  componentDidMount() {
    this.loadFlowMetricsData();
  }

  componentDidUpdate(prevProps) {
    // Reload data if lookback period or plan_id changes
    const { lookback, plan_id } = this.props;
    if (prevProps.lookback !== lookback || prevProps.plan_id !== plan_id) {
      this.loadFlowMetricsData();
    }
  }

  componentWillUnmount() {
    if (this.activeRawQuery) {
      this.activeRawQuery.cancel();
    }
    if (this.activeReceivedQuery) {
      this.activeReceivedQuery.cancel();
    }
    if (this.activeStoredQuery) {
      this.activeStoredQuery.cancel();
    }
  }

  loadFlowMetricsData = async () => {
    // Map the simple value to ISO 8601 duration format, default to 1 day if not provided
    const { $query, $setup, lookback: lookbackProp = '1_day', plan_id, groupByProvider = false } = this.props;
    const lookback = this.timePeriodToLookback[lookbackProp] || this.timePeriodToLookback['1_day'];
    const deviceFlowWindowSize = this.timePeriodToWindowSize[lookbackProp] || this.timePeriodToWindowSize['1_day'];

    // Determine dimensions based on query type
    // Raw flow query: Uses cloud_provider dimension when grouping by provider
    const rawFlowDimensions = groupByProvider ? ['plan_id', 'cloud_provider'] : ['plan_id'];

    // Received/Stored flow queries: Use device id (device_id) dimension when grouping by provider
    const deviceFlowDimensions = groupByProvider ? ['plan_id', 'device_id'] : ['plan_id'];

    // When groupByProvider=true, 'device_id' must NOT be a merge dimension because the kmetrics SQL generator
    // excludes merge dimensions from the final SELECT, making model.get('device_id') always undefined.
    // Without 'device_id' in the output, processProviderData cannot map devices to cloud providers.
    // When groupByProvider=false, merging by 'device_id' correctly aggregates all devices into plan-level metrics.
    const deviceFlowMerge = groupByProvider ? null : { dimensions: ['device_id'], aggregate: 'sum' };

    // When groupByProvider=true the query returns one row per device, and processProviderData
    // only aggregates devices that have timeseries data.  deviceFlowIncludeTimeseries must equal
    // deviceFlowLimit so that every returned device contributes to the provider totals — otherwise
    // devices ranked beyond deviceFlowIncludeTimeseries are silently omitted and provider values
    // are undercounted.
    // When groupByProvider=false the merge collapses all devices into a single plan-level row,
    // so deviceFlowLimit and deviceFlowIncludeTimeseries are effectively irrelevant (there is
    // always exactly 1 result row).

    const deviceFlowLimit = groupByProvider ? NON_MERGE_DEVICE_LIMIT : MERGE_DEVICE_LIMIT;
    const deviceFlowIncludeTimeseries = groupByProvider ? NON_MERGE_DEVICE_LIMIT : MERGE_DEVICE_LIMIT;

    let filterGroup;
    if (plan_id) {
      // If a specific plan_id is provided, add it as a filter
      filterGroup = buildFilterGroup({
        connector: 'Any',
        filters: [
          {
            filterField: 'plan_id',
            operator: '=',
            filterValue: `${plan_id}`
          }
        ],
        name: 'Plan Filter Group'
      });
    } else {
      const cloudPlans = await $setup.getCloudPlans();
      if (cloudPlans.length === 0) {
        console.warn('⚠️ No active cloud plans found for this company');
      }

      const planFilters = cloudPlans.map((plan) => ({
        filterField: 'plan_id',
        operator: '=',
        filterValue: `${plan.id}`
      }));

      filterGroup = buildFilterGroup({
        connector: 'Any',
        filters: planFilters,
        name: 'Plan Filter Group'
      });
    }

    // Query structure for raw flow metrics
    const rawFlowQuery = {
      update_frequency: 0,
      use_kmetrics: true,
      show_overlay: false,
      show_total_overlay: false,
      kmetrics: {
        dimensions: rawFlowDimensions,
        range: {
          lookback
        },
        limit: 100,
        includeTimeseries: 20,
        streamingUpdate: false,
        filters: {
          connector: 'All',
          filterGroups: [filterGroup]
        },
        measurement: '/kentik/licensing/flow/cloud',
        metrics: [
          {
            name: 'in',
            type: 'gauge'
          }
        ],
        merge: {
          dimensions: ['hostname', 'device_id'],
          aggregate: 'sum'
        },
        window: {
          size: deviceFlowWindowSize,
          fn: {
            in: 'max'
          }
        },
        rollups: {
          last_in: {
            metric: 'in',
            aggregate: 'last'
          },
          p95_in: {
            metric: 'in',
            aggregate: 'p95'
          }
        },
        sort: [
          {
            name: 'last_in',
            direction: 'desc'
          }
        ],
        zeroFill: true,
        viz: {
          type: 'line',
          rollup: 'last_in',
          metric: 'in'
        }
      }
    };

    // Query structure for received flow metrics
    const receivedFlowQuery = {
      update_frequency: 0,
      use_kmetrics: true,
      show_overlay: false,
      show_total_overlay: false,
      kmetrics: {
        dimensions: deviceFlowDimensions,
        range: {
          lookback
        },
        limit: deviceFlowLimit,
        includeTimeseries: deviceFlowIncludeTimeseries,
        streamingUpdate: false,
        filters: {
          connector: 'All',
          filterGroups: [filterGroup]
        },
        measurement: '/kentik/licensing/flow',
        metrics: [
          {
            name: 'in',
            type: 'gauge'
          }
        ],
        ...(deviceFlowMerge ? { merge: deviceFlowMerge } : {}),
        window: {
          size: deviceFlowWindowSize,
          fn: {
            in: 'max'
          }
        },
        rollups: {
          p95_in: {
            metric: 'in',
            aggregate: 'p95'
          }
        },
        sort: [
          {
            name: 'p95_in',
            direction: 'desc'
          }
        ],
        zeroFill: true,
        viz: {
          type: 'line',
          rollup: 'p95_in',
          metric: 'in'
        }
      }
    };

    // Query structure for stored flow metrics
    const storedFlowQuery = {
      update_frequency: 0,
      use_kmetrics: true,
      show_overlay: false,
      show_total_overlay: false,
      kmetrics: {
        dimensions: deviceFlowDimensions,
        range: {
          lookback
        },
        limit: deviceFlowLimit,
        includeTimeseries: deviceFlowIncludeTimeseries,
        streamingUpdate: false,
        filters: {
          connector: 'All',
          filterGroups: [filterGroup]
        },
        measurement: '/kentik/licensing/flow',
        metrics: [
          {
            name: 'out',
            type: 'gauge'
          }
        ],
        ...(deviceFlowMerge ? { merge: deviceFlowMerge } : {}),
        window: {
          size: deviceFlowWindowSize,
          fn: {
            out: 'max'
          }
        },
        rollups: {
          p95_out: {
            metric: 'out',
            aggregate: 'p95'
          }
        },
        sort: [
          {
            name: 'p95_out',
            direction: 'desc'
          }
        ],
        zeroFill: true,
        viz: {
          type: 'line',
          rollup: 'p95_out',
          metric: 'out'
        }
      }
    };

    // Store query objects as instance properties so they can be passed to children for MetricsExplorerButton
    this.rawFlowQuery = rawFlowQuery;
    this.receivedFlowQuery = receivedFlowQuery;
    this.storedFlowQuery = storedFlowQuery;

    try {
      this.setState({ loading: true, error: null });

      // Execute all three queries in parallel
      this.activeRawQuery = makeCancelable($query.runQuery(rawFlowQuery, false, true));
      this.activeReceivedQuery = makeCancelable($query.runQuery(receivedFlowQuery, false, true));
      this.activeStoredQuery = makeCancelable($query.runQuery(storedFlowQuery, false, true));

      const [rawResponse, receivedResponse, storedResponse] = await Promise.all([
        this.activeRawQuery.promise,
        this.activeReceivedQuery.promise,
        this.activeStoredQuery.promise
      ]);

      this.setState({
        loading: false,
        rawFlowModel: rawResponse.results,
        receivedFlowModel: receivedResponse.results,
        storedFlowModel: storedResponse.results,
        error: null
      });
    } catch (error) {
      if (error instanceof CanceledError) {
        // Query was canceled, ignore
        return;
      }
      console.error('Failed to load flow metrics data:', error);
      this.setState({
        loading: false,
        error: error.message || 'Failed to load data',
        rawFlowModel: null,
        receivedFlowModel: null,
        storedFlowModel: null
      });
    }
  };

  /**
   * Build a mapping from device ID to cloud provider using $clouds.collection.
   * Returns an object like: { '12345': 'aws', '12346': 'gcp', ... }
   */
  buildDeviceToProviderMap = () => {
    const { $clouds } = this.props;
    const deviceToProvider = {};

    if (!$clouds || !$clouds.collection) {
      console.warn('⚠️ $clouds.collection not available');
      return deviceToProvider;
    }

    // Iterate through all cloud exports using Collection's each method
    $clouds.collection.each((cloudExport) => {
      const cloudProvider = cloudExport.get('cloud_provider');
      const devices = cloudExport.get('devices');

      // Map each device to its cloud provider
      if (Array.isArray(devices)) {
        devices.forEach((device) => {
          const deviceId = device.id || device.device_id;
          if (deviceId) {
            // Normalize GCE to GCP
            const normalizedProvider = cloudProvider === 'gce' ? 'gcp' : cloudProvider;
            deviceToProvider[String(deviceId)] = normalizedProvider;
          }
        });
      }
    });

    return deviceToProvider;
  };

  /**
   * Process flow model data to separate by cloud provider.
   * Returns an object with provider names as keys and timeseries data as values.
   * Example: { aws: [...datapoints], gcp: [...datapoints], azure: [...datapoints] }
   *
   * For device-based queries (received/stored), maps device IDs to providers and aggregates
   * timeseries data across multiple devices for the same provider.
   * For exporter-based queries (raw), uses cloud_provider dimension directly.
   */
  processProviderData = (flowModel, metricName, useDeviceMapping = false) => {
    if (!flowModel || !flowModel.models) {
      console.warn('⚠️ No flowModel or models available');
      return {};
    }

    const providerData = {};
    const deviceToProvider = useDeviceMapping ? this.buildDeviceToProviderMap() : {};

    flowModel.models.forEach((model) => {
      const metricsArr = model.get('metrics');
      const dimensions = model.get('dimensions');
      const timeseries = model.get('timeseries');

      if (metricsArr && metricsArr[0] === metricName) {
        let cloudProvider;

        if (useDeviceMapping) {
          // For device-based queries, map device ID to provider
          const deviceId = model.get('device_id') || dimensions?.device_id;
          if (deviceId) {
            cloudProvider = deviceToProvider[String(deviceId)];
            if (!cloudProvider) {
              console.warn(`⚠️ No provider mapping found for device ID: ${deviceId}`);
            }
          } else {
            console.warn('⚠️ No device ID (device_id) in dimensions:', dimensions);
          }
        } else {
          // For exporter-based queries, use cloud_provider dimension
          cloudProvider = model.get('cloud_provider') || dimensions?.cloud_provider;

          if (!cloudProvider) {
            console.warn('⚠️ No cloud_provider in dimensions:', dimensions);
          }

          if (cloudProvider === 'gce') {
            cloudProvider = 'gcp';
          }
        }

        if (cloudProvider && timeseries && Array.isArray(timeseries)) {
          // Aggregate timeseries data by provider
          if (!providerData[cloudProvider]) {
            // First device for this provider - initialize with a copy of the timeseries
            providerData[cloudProvider] = timeseries.map(([timestamp, value]) => [timestamp, value]);
          } else {
            // Additional device for this provider - sum the values at each timestamp
            const existingData = providerData[cloudProvider];
            const timestampMap = new Map(existingData);

            timeseries.forEach(([timestamp, value]) => {
              const currentValue = timestampMap.get(timestamp) || 0;
              const safeValue = typeof value === 'number' ? value : 0;
              timestampMap.set(timestamp, currentValue + safeValue);
            });

            // Convert back to array format and sort by timestamp
            providerData[cloudProvider] = Array.from(timestampMap.entries()).sort((a, b) => a[0] - b[0]);
          }
        }
      }
    });

    return providerData;
  };

  render() {
    const { children, groupByProvider = false } = this.props;
    const { loading, rawFlowModel, receivedFlowModel, storedFlowModel, error } = this.state;

    if (typeof children === 'function') {
      // If groupByProvider is enabled, process data by provider
      if (groupByProvider) {
        // Raw flow uses cloud_provider dimension (exporter-based)
        const rawFlowByProvider = this.processProviderData(rawFlowModel, 'in', false);
        // Received and stored flows use device ID mapping (device-based)
        const receivedFlowByProvider = this.processProviderData(receivedFlowModel, 'in', true);
        const storedFlowByProvider = this.processProviderData(storedFlowModel, 'out', true);

        return children({
          loading,
          rawFlowModel,
          receivedFlowModel,
          storedFlowModel,
          rawFlowByProvider,
          receivedFlowByProvider,
          storedFlowByProvider,
          rawFlowQuery: this.rawFlowQuery,
          receivedFlowQuery: this.receivedFlowQuery,
          storedFlowQuery: this.storedFlowQuery,
          error,
          retry: this.loadFlowMetricsData
        });
      }

      // Default behavior for License page (single plan, no provider separation)
      return children({
        loading,
        rawFlowModel,
        receivedFlowModel,
        storedFlowModel,
        rawFlowQuery: this.rawFlowQuery,
        receivedFlowQuery: this.receivedFlowQuery,
        storedFlowQuery: this.storedFlowQuery,
        error,
        retry: this.loadFlowMetricsData
      });
    }

    if (loading) {
      return <Text color="muted">Loading flow metrics data...</Text>;
    }

    if (error) {
      return (
        <Box p={2} backgroundColor="#fff2f0" borderRadius="3px" border="1px solid #ffccc7">
          <Text color="danger">Error loading flow metrics data: {error}</Text>
        </Box>
      );
    }

    return children;
  }
}
